import shopifyFormatMoney, {
  isValidShopifyMoneyFormat,
} from './shopifyFormatMoney';
import { logError } from '../utils/logging';
import customLocalStorage, { customSessionStorage } from './storage';
import { REGISTERED_APPS } from '../types';

// Prefer client-side currency switchers when available
function getActiveCurrency(): string {
  return (
    (window as any)?.Currency?.currentCurrency ||
    (window as any)?.Shopify?.currency?.active ||
    (window as any)?.ShopifyAnalytics?.meta?.currency ||
    'USD'
  );
}

function convertPrice(amount: number) {
  const moneyFormatFunction = (price: number | undefined) => {
    if (typeof price === 'undefined') return amount.toString();
    const currencyConfiguration = getCurrency();
    const convertedAmount = currencyConfiguration.convert
      ? currencyConfiguration.convert(price)
      : price;
    return currencyConfiguration.format
      ? currencyConfiguration.format(convertedAmount)
      : price.toString();
  };

  if (typeof window.CANDYRACK_CUSTOM_FORMAT_MONEY !== 'function') {
    window.CANDYRACK_CUSTOM_FORMAT_MONEY = moneyFormatFunction;
  }
  if (typeof window.CANDYCART_CUSTOM_FORMAT_MONEY !== 'function') {
    window.CANDYCART_CUSTOM_FORMAT_MONEY = moneyFormatFunction;
  }

  return amount;
}

interface CurrencyConfiguration {
  convert?: (amount: number) => number;
  format?: (amount: number) => string;
  currency: string;
}

function getCurrency(): CurrencyConfiguration {
  const storeCurrency = window.Shopify?.currency.active;

  // temp fix for https://github.com/digismoothie/candyrack/pull/662 -- https://digismoothie.atlassian.net/browse/APPS-2132
  // by doing this there is an assumption we are on admin and not client in reality
  if (!window.CandyRack && !window.CandyCart && !window.LastUpsell) {
    return {
      convert: (amount) => amount,
      format: (amount) => formatMoney(amount * 100),
      currency: storeCurrency,
    };
  }

  if (window.CandyRack?.currency?.configuration) {
    return window.CandyRack.currency.configuration;
  }

  if (window.LastUpsell?.currency?.configuration) {
    return window.LastUpsell.currency.configuration;
  }

  if (window.DoublyGlobalCurrency) {
    return {
      convert: (amount) =>
        window.DoublyGlobalCurrency.convert(
          amount * 100,
          storeCurrency,
          window.DoublyGlobalCurrency.currentCurrency,
        ),
      format: (amount) =>
        window.DoublyGlobalCurrency.formatMoney(
          amount,
          window.DoublyGlobalCurrency.moneyFormats[
            window.DoublyGlobalCurrency.currentCurrency
          ][window.DoublyGlobalCurrency.format],
          true,
          false,
        ),
      currency: window.DoublyGlobalCurrency.currentCurrency,
    };
  }

  if (window.ACSCurrency && window.ACSCurrency.currentCurrency) {
    const convertFn = (amount: number) =>
      window.ACSCurrency.convert && window.ACSCurrency.currentCurrency
        ? window.ACSCurrency.convert(
            amount * 100,
            storeCurrency,
            window.ACSCurrency.currentCurrency,
          )
        : amount;
    const formatFn = (amount: number) =>
      window.ACSCurrency.formatMoney &&
      window.ACSCurrency.moneyFormats &&
      window.ACSCurrency.currentCurrency
        ? window.ACSCurrency.formatMoney(
            amount,
            window.ACSCurrency.moneyFormats[window.ACSCurrency.currentCurrency][
              window.ACSCurrency.format
            ],
          )
        : formatMoneyIntl(amount, storeCurrency);
    return {
      convert: convertFn,
      format: formatFn,
      currency: window.ACSCurrency.currentCurrency,
    };
  }

  const vitalsCurrency = customLocalStorage.getItem('__v_cc__s_c__');
  if (vitalsCurrency && vitalsCurrency.length === 3) {
    const convertFn = (amount: number) =>
      window.Currency && window.Currency.convert
        ? window.Currency.convert(amount, storeCurrency, vitalsCurrency)
        : amount;
    return {
      convert: convertFn,
      format: (amount) => formatMoneyIntl(amount, vitalsCurrency),
      currency: vitalsCurrency,
    };
  }

  if (window.BOLDCURRENCY && window.BOLDCURRENCY.enabled) {
    return {
      convert: (amount) =>
        window.BOLDCURRENCY.converter.convertPrice(
          amount * 100,
          window.BOLDCURRENCY.currentCurrency,
        ),
      format: (amount) => window.BOLDCURRENCY.converter.formatMoney(amount),
      currency: window.BOLDCURRENCY.currentCurrency,
    };
  }

  if (window.conversionBearAutoCurrencyConverter) {
    return {
      format: (amount) => {
        const { amount: convertedAmount, default_format } =
          window.conversionBearAutoCurrencyConverter!.convert(amount);
        return default_format.replace(/\{{[^)]*\}}/g, convertedAmount);
      },
      currency: window.conversionBearAutoCurrencyConverter.currency,
    };
  }

  if (
    window.LangShop &&
    typeof window.LangShop.getCurrencyCode === 'function' &&
    typeof window.LangShop.langShop.currencyConverter === 'function'
  ) {
    return {
      convert: (amount) =>
        window.LangShop!.langShop.currencyConverter.convert(
          amount * 100,
          storeCurrency.toLowerCase(),
          window.LangShop!.getCurrencyCode().toLowerCase(),
        ),
      format: (amount) => window.LangShop!.formatMoney(amount),
      currency: window.LangShop.getCurrencyCode(),
    };
  }

  let baCurrency = customSessionStorage.getItem('bacurr_user_cur') || '';
  baCurrency = baCurrency.slice(1, -1);
  if (
    window.baCurr &&
    baCurrency.length === 3 &&
    typeof window.baCurr.widgetConv === 'function'
  ) {
    return {
      format: (amount) => window.baCurr!.widgetConv(amount, baCurrency),
      currency: baCurrency,
    };
  }

  const autoketingCurrency = customLocalStorage.getItem(
    'autoketing-currency-location-customer-v1',
  );
  if (autoketingCurrency && autoketingCurrency.length === 3) {
    return {
      format: (amount) => {
        let formattedAmount = amount.toString();
        applyDOMObservingFormatter(amount, 'span', 'money').then(
          (result) => (formattedAmount = result),
        );
        return formattedAmount;
      },
      currency: autoketingCurrency,
    };
  }

  if (
    window.SECOMAPP &&
    window.SECOMAPP.cookie &&
    typeof window.SECOMAPP.cookie === 'function'
  ) {
    const getMoneyFormat = () => {
      if (
        window.Currency &&
        window.Currency.moneyFormats &&
        window.Currency.format &&
        window.Currency.currentCurrency
      ) {
        return window.Currency.moneyFormats[window.Currency.currentCurrency][
          window.Currency.format
        ];
      }
      if (window.theme?.moneyFormat) {
        return window.theme.moneyFormat;
      }

      /* When window.SECOMAPP was introduced, it seems
       ** it was relevant for the CR (add to cart) situation.
       ** Given how our code has evolved, it make sense
       ** to also check for LU (thank you page) - though
       ** it could be irrelevant for our case.
       */
      return getMoneyFormatFromWindow();
    };
    const moneyFormat = getMoneyFormat();
    let formatFn = (amount: number) => formatMoneyIntl(amount, storeCurrency);
    if (window.Currency?.formatMoney && moneyFormat) {
      formatFn = (amount: number) => {
        const cents = window.Currency!.convert(
          amount,
          window.Currency!.shopCurrency,
          window.Currency!.currentCurrency,
        );
        return window.Currency!.formatMoney(cents, moneyFormat);
      };
    }
    if (window.Shopify.formatMoney && moneyFormat)
      formatFn = (amount) =>
        window.Shopify.formatMoney(amount, moneyFormat, false);
    if (window.Shopify.api && window.Shopify.api.formatMoney && moneyFormat)
      formatFn = (amount) =>
        window.Shopify.api.formatMoney(amount, moneyFormat, false);
    if (window.theme && window.theme.Currency && moneyFormat)
      formatFn = (amount) =>
        window.theme!.Currency!.formatMoney(amount, moneyFormat, false);
    return {
      format: (amount) => formatFn(amount * 100),
      currency: window.Currency?.currentCurrency || storeCurrency,
    };
  }

  return {
    convert: (amount) => amount,
    format: (amount) => formatMoney(amount * 100),
    currency: storeCurrency,
  };
}

function applyDOMObservingFormatter(
  cents: number,
  element: string,
  className: string,
) {
  const toConvert = window.document.createElement(element);
  toConvert.classList.add(className);
  toConvert.innerHTML = cents.toString();
  window.document.body.append(toConvert);
  return new Promise<string>((resolve, reject) => {
    new MutationObserver((_, observer) => {
      resolve(toConvert.innerHTML);
      observer.disconnect();
    }).observe(toConvert, { childList: true, subtree: true });
  });
}

function formatMoneyIntl(cents: number, currency?: string) {
  const amount = cents / 100;
  const limitedAmount = amount < 0 ? 0 : amount;

  const activeCurrency = currency ?? getActiveCurrency();
  if (window.Intl && window.navigator) {
    return new Intl.NumberFormat(window.navigator.language, {
      style: 'currency',
      currency: activeCurrency,
    }).format(limitedAmount);
  }

  return `${limitedAmount} ${activeCurrency}`;
}

function getFormatFromCurrency(currency: any): string | undefined {
  if (!currency || currency.defaultFormat === true) {
    return undefined;
  }

  if (currency.moneyFormats) {
    return (
      currency.moneyFormats.money_with_currency_format ||
      currency.moneyFormats.money_format ||
      (currency.format ? currency.moneyFormats[currency.format] : undefined)
    );
  }
  return undefined;
}
const stripHtml = (html: string) => {
  return html.replace(/<[^>]*>/g, '');
};

function getMoneyFormatFromWindow(): string | undefined {
  const candyrackFormatFromCurrency = getFormatFromCurrency(
    window.CandyRack?.currency,
  );
  if (candyrackFormatFromCurrency) {
    return stripHtml(candyrackFormatFromCurrency);
  }
  const candycartFormatFromCurrency = getFormatFromCurrency(
    (window as any).CandyCart?.currency,
  );
  if (candycartFormatFromCurrency) {
    return stripHtml(candycartFormatFromCurrency);
  }
  const lastUpsellFormatFromCurrency = getFormatFromCurrency(
    window.LastUpsell?.currency,
  );
  if (lastUpsellFormatFromCurrency) {
    return stripHtml(lastUpsellFormatFromCurrency);
  }
  const pppMoneyFormats = (window as any).CANDYRACK_PPP_MONEY_FORMATS as
    | { money_format?: string; money_with_currency_format?: string }
    | undefined;
  if (pppMoneyFormats) {
    const activeCurrencyUpper = (getActiveCurrency() || '').toUpperCase();
    const baseStoreCurrencyUpper = (
      (pppMoneyFormats as any).currencyCode || ''
    ).toUpperCase();
    const pppTemplate =
      pppMoneyFormats.money_with_currency_format ||
      pppMoneyFormats.money_format;
    const activeAndStoreCurrenciesMatch =
      baseStoreCurrencyUpper &&
      activeCurrencyUpper &&
      baseStoreCurrencyUpper === activeCurrencyUpper;

    if (pppTemplate && activeAndStoreCurrenciesMatch) {
      return stripHtml(pppTemplate);
    }
  }
  return undefined;
}
function formatMoney(cents: number) {
  const normalizedCents = Math.abs(cents / 100) < 0.1 ? 0 : cents;

  const storeFormat = getMoneyFormatFromWindow();

  // Check if storeFormat exists first
  if (!storeFormat) {
    return formatMoneyIntl(normalizedCents, getActiveCurrency());
  }

  const isValid = isValidShopifyMoneyFormat(storeFormat);

  if (!isValid) {
    return formatMoneyIntl(normalizedCents, getActiveCurrency());
  }

  try {
    const result = shopifyFormatMoney(normalizedCents, storeFormat);

    // Check if the result still contains unprocessed placeholders
    // This indicates that shopifyFormatMoney couldn't process the format properly
    if (result.includes('{{') && result.includes('}}')) {
      logError(
        `shopifyFormatMoney returned unprocessed placeholders, falling back to Intl.NumberFormat. Format: ${storeFormat}, Result: ${result}`,
      );
      return formatMoneyIntl(normalizedCents, getActiveCurrency());
    }

    return result;
  } catch (error) {
    logError(
      'shopifyFormatMoney failed, falling back to Intl.NumberFormat',
      error as REGISTERED_APPS | undefined,
    );
    return formatMoneyIntl(normalizedCents, getActiveCurrency());
  }
}

function calculateDiscountedPrice(
  originalPrice: number,
  discountPercentage: number,
): number {
  const discountedPrice = originalPrice * (1 - discountPercentage / 100);

  // Round to 0 if result is less than 0.1 to handle floating-point precision
  // from 100% or near-100% discounts
  if (Math.abs(discountedPrice) < 0.1) {
    return 0;
  }

  return discountedPrice;
}

/*
 ** It seems that we don't always have window.Shopify.checkout information available
 ** when we expect it. Using this approach we can give it some time to load.
 ** As we are not sure on why the checkout object remains empty we can't be sure
 ** that this fix will improve the situation. Its worth however trying it out.
 **
 ** Please not that window.Shopify.checkout is a different object from window.Shopify.Checkout
 */
function waitForShopifyCheckoutProperty(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const intervalId = setInterval(() => {
      if (window.Shopify && window.Shopify.checkout) {
        clearInterval(intervalId);
        resolve();
      }
    }, 100);

    setTimeout(() => {
      clearInterval(intervalId);
      reject(
        new Error('Timeout waiting for Shopify.checkout to become available'),
      );
    }, 3000);
  });
}

export {
  getCurrency,
  convertPrice,
  formatMoney,
  calculateDiscountedPrice,
  waitForShopifyCheckoutProperty,
  getMoneyFormatFromWindow,
};
